import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import BaseBreadcrumbs from "../../components/BaseBreadcrumbs";
import Pagination from "../../components/Pagination"; 
import {normalizeMeta}  from "../../utils/normalizeMeta"
// ---------------- UI primitives ----------------
const cx = (...classes) => classes.filter(Boolean).join(" ");

const Button = ({ as: Tag = "button", className = "", children, ...rest }) => (
	<Tag
		className={cx(
			"inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium",
			"border bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700/80",
			"focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60",
			"disabled:opacity-50 disabled:cursor-not-allowed",
			className
		)}
		{...rest}
	>
		{children}
	</Tag>
);

const DangerButton = (props) => (
	<Button
		{...props}
		className={cx(
			"border-red-800 bg-red-900/35 text-red-100 hover:bg-red-900/55",
			props.className
		)}
	/>
);

const PrimaryButton = (props) => (
	<Button
		{...props}
		className={cx(
			"border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-500",
			props.className
		)}
	/>
);

const Card = ({ children }) => (
	<section>
		<div>{children}</div>
	</section>
);

const Field = ({ label, children, help }) => (
	<label className="grid gap-1">
		<span className="text-xxl text-slate-100 uppercase tracking-wide">
			{label}
		</span>
		{children}
		{help ? (
			<span className="text-[11px] text-slate-500">{help}</span>
		) : null}
	</label>
);

const Input = (props) => (
	<input
		{...props}
		className={cx(
			"h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200",
			"placeholder:text-slate-500",
			"focus:outline-none focus:ring-2 focus:ring-indigo-500/60",
			props.className
		)}
	/>
);

const Textarea = (props) => (
	<textarea
		{...props}
		className={cx(
			"px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200",
			"placeholder:text-slate-500 min-h-[88px]",
			"focus:outline-none focus:ring-2 focus:ring-indigo-500/60",
			props.className
		)}
	/>
);

const Modal = ({ open, title, onClose, children, footer }) => {
	if (!open) return null;
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/60" onClick={onClose} />
			<div className="relative w-[min(720px,92vw)] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
				<div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-slate-800">
					<h3 className="text-lg font-semibold text-slate-100">
						{title}
					</h3>
					<button
						onClick={onClose}
						className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800"
						aria-label="Close"
					>
						×
					</button>
				</div>
				<div className="p-4 md:p-6 space-y-4">{children}</div>
				{footer ? (
					<div className="px-4 md:px-6 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
						{footer}
					</div>
				) : null}
			</div>
		</div>
	);
};

const pickKnown = (obj) => {
	if (!obj || typeof obj !== "object") return {};
	const keys = ["code", "name", "description"];
	const out = {};
	keys.forEach((k) => {
		if (k in obj) out[k] = obj[k] ?? "";
	});
	keys.forEach((k) => {
		if (!(k in out)) out[k] = "";
	});
	return out;
};

const prettyJSON = (value) => {
	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return String(value ?? "");
	}
};

// ---------------- Main Page ----------------
const PlanCategoryManager = () => {
	// Endpoints
	const API_LIST_BASE = "/admin/plan-categories"; // GET list/detail
	const API_ADMIN_BASE = "/admin/plan-categories"; // POST/PUT/DELETE

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const [rows, setRows] = useState([]);

	const [search, setSearch] = useState("");
	const [meta, setMeta] = useState({
		skip: 0,
		limit: 10,
		total: 0,
	});

	const [creating, setCreating] = useState(false);
	const [createForm, setCreateForm] = useState({
		code: "",
		name: "",
		description: "",
	});
	const [editing, setEditing] = useState(null);
	const [editForm, setEditForm] = useState({
		code: "",
		name: "",
		description: "",
	});
	const [confirmDel, setConfirmDel] = useState(null);

	const fetchList = useCallback(
		async ({ page, page_size, search: q }) => {
			setLoading(true);
			setError("");
			try {
				const params = {
					page,
					page_size,
					skip: (page - 1) * page_size,
					limit: page_size,
					search: q ?? "",
				};
				const res = await axiosInstance.get(`${API_LIST_BASE}/`, {
					params,
				});

				const data = res?.data;
				const items = Array.isArray(data)
					? data
					: Array.isArray(data?.items)
					? data.items
					: [];
				const nextMeta = Array.isArray(data) ? undefined : data?.meta;

				setRows(items);
				const nm = normalizeMeta(nextMeta, params, items.length);
				setMeta(nm);
			} catch (e) {
				console.error(e);
				setError(
					"Failed to load plan categories. Check API or network."
				);
				setRows([]);
			} finally {
				setLoading(false);
			}
		},
		[API_LIST_BASE]
	);

	useEffect(() => {
		fetchList({ page: 1, page_size: meta.limit, search });
	}, []);

	const columnKeys = useMemo(() => {
		const sample = rows[0] || {};
		const base = ["id", "code", "name"];
		const extra = Object.keys(sample)
			.filter(
				(k) =>
					!base.includes(k) &&
					!["created_at", "updated_at"].includes(k)
			)
			.slice(0, 4);
		return [...base, ...extra];
	}, [rows]);

	// CRUD
	const onCreate = async () => {
		const payload = pickKnown(createForm);
		if (!payload.code?.trim() || !payload.name?.trim()) {
			setError("Code and Name are required.");
			return;
		}
		setLoading(true);
		setError("");
		try {
			await axiosInstance.post(`${API_ADMIN_BASE}/`, payload);
			setCreating(false);
			setCreateForm({ code: "", name: "", description: "" });
			await fetchList({ page: 1, page_size: meta.limit || 10, search });
		} catch (e) {
			console.error(e);
			setError(
				String(
					e?.response?.data?.detail ||
						"Failed to create plan category"
				)
			);
		} finally {
			setLoading(false);
		}
	};

	const startEdit = (row) => {
		setEditing(row);
		setEditForm(pickKnown(row));
	};

	const getCurrentPage = () =>
		meta?.page ||
		(meta?.skip != null && meta?.limit
			? Math.floor(meta.skip / meta.limit) + 1
			: 1);

	const reloadCurrentPageSafe = async () => {
		const curr = getCurrentPage();
		await fetchList({
			page: curr,
			page_size: meta.limit || meta.page_size || 10,
			search,
		});
		if (
			!loading &&
			rows.length === 0 &&
			(meta?.total !== undefined ? curr > 1 : true)
		) {
			await fetchList({
				page: Math.max(1, curr - 1),
				page_size: meta.limit || meta.page_size || 10,
				search,
			});
		}
	};

	const onUpdate = async () => {
		if (!editing) return;
		const id = editing.id ?? editing.plan_category_id;
		if (!id) return setError("Missing ID for update.");
		const payload = pickKnown(editForm);
		setLoading(true);
		setError("");
		try {
			await axiosInstance.put(`${API_ADMIN_BASE}/${id}`, payload);
			setEditing(null);
			await reloadCurrentPageSafe();
		} catch (e) {
			console.error(e);
			setError(String(e?.response?.data?.detail || "Failed to update"));
		} finally {
			setLoading(false);
		}
	};

	const onDelete = async () => {
		if (!confirmDel) return;
		const id = confirmDel.id ?? confirmDel.plan_category_id;
		setLoading(true);
		setError("");
		try {
			await axiosInstance.delete(`${API_ADMIN_BASE}/${id}`);
			setConfirmDel(null);
			await reloadCurrentPageSafe();
		} catch (e) {
			console.error(e);
			setError(String(e?.response?.data?.detail || "Failed to delete"));
		} finally {
			setLoading(false);
		}
	};

	const totalLabel = useMemo(() => {
		const total = Number(meta.total ?? 0);
		return total > 0 ? total : rows.length;
	}, [meta.total, rows.length]);

	return (
		<div className="bg-gradient-to-br from-black via-gray-900 to-gray-800 text-gray-100 min-h-screen">
			<div className="max-w-full mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
				{/* Breadcrumbs */}
				<div>
					<BaseBreadcrumbs />
				</div>

				{/* Header + actions */}
				<div className="flex flex-col md:flex-row md:items-center gap-3">
					<h1 className="text-2xl md:text-3xl font-bold tracking-tight">
						Plan Categories
					</h1>
					<div className="flex flex-wrap gap-2 md:ml-auto">
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter")
									fetchList({
										page: 1,
										page_size:
											meta.limit || meta.page_size || 10,
										search,
									});
							}}
							placeholder="Search by code or name…"
							className="w-64"
							aria-label="Search"
						/>
						<Button onClick={() => setSearch("")}>Clear</Button>
						<Button
							onClick={() =>
								fetchList({
									page: 1,
									page_size:
										meta.limit || meta.page_size || 10,
									search,
								})
							}
							disabled={loading}
							aria-busy={loading}
						>
							{loading ? "Loading…" : "Search"}
						</Button>
						<PrimaryButton
							onClick={() => {
								setCreating(true);
								setCreateForm({
									code: "",
									name: "",
									description: "",
								});
							}}
						>
							New
						</PrimaryButton>
					</div>
				</div>

				{/* Error banner */}
				{error && (
					<div className="rounded-xl border border-red-800/50 bg-red-900/35 text-red-100 px-4 py-3">
						{error}
					</div>
				)}

				<Card>
					<div className="overflow-auto rounded-xl border border-slate-800/80">
						<table className="min-w-full table-auto text-sm">
							{" "}
							<colgroup>
								{columnKeys.map((k) => (
									<col key={k} />
								))}
								<col className="w-44" />{" "}
							</colgroup>
							<thead className="bg-slate-900/80 sticky top-0 z-10 border-b border-slate-800/80">
								<tr>
									{columnKeys.map((k) => (
										<th
											key={k}
											scope="col"
											className="text-left uppercase tracking-wide text-[11px] text-slate-100 px-3 py-2"
										>
											{k.replaceAll("_", " ")}
										</th>
									))}
									<th
										className={cx(
											"w-44 shrink-0 text-left uppercase tracking-wide text-[11px] text-slate-100 px-3 py-2"
										)}
									>
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-800/80">
								{loading && rows.length === 0 ? (
									[...Array(5)].map((_, i) => (
										<tr key={i} className="animate-pulse">
											{columnKeys.map((k) => (
												<td
													key={k}
													className="px-3 py-3"
												>
													<div className="h-3 rounded bg-slate-700/50" />
												</td>
											))}
											<td className="px-3 py-3 text-right w-44 shrink-0">
												<div className="h-8 w-40 rounded bg-slate-700/50 ml-auto" />
											</td>
										</tr>
									))
								) : rows.length > 0 ? (
									rows.map((row) => (
										<tr
											key={row.id ?? row.code}
											className="odd:bg-slate-900/20 hover:bg-slate-800/30 transition-colors"
										>
											{columnKeys.map((k) => (
												<td
													key={k}
													className={cx(
														"px-3 py-3 align-middle",
														k === "name" &&
															"font-medium text-slate-100"
													)}
												>
													{row[k] === null ||
													row[k] === undefined ||
													row[k] === ""
														? "—"
														: String(row[k])}
												</td>
											))}
											<td
												className={cx(
													"px-3 py-2 align-middle text-left w-44 shrink-0 whitespace-nowrap"
												)}
											>
												<div className="inline-flex items-center gap-2">
													<Button
														onClick={() =>
															startEdit(row)
														}
														className="h-8 px-2"
													>
														Edit
													</Button>
													<DangerButton
														onClick={() =>
															setConfirmDel(row)
														}
														className="h-8 px-2"
													>
														Delete
													</DangerButton>
												</div>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td
											colSpan={columnKeys.length + 1}
											className="py-12 text-center text-slate-400"
										>
											No plan categories found.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					{rows.length > 0 && (
						<div className="mt-4">
							<Pagination
								meta={meta}
								onChange={({ page, page_size }) =>
									fetchList({ page, page_size, search })
								}
								pageSizeOptions={[ 25, 40,60, 100]}
							/>
						</div>
					)}
				</Card>

				{/* Create Modal */}
				<Modal
					open={creating}
					title="Create Plan Category"
					onClose={() => setCreating(false)}
					footer={
						<>
							<Button onClick={() => setCreating(false)}>
								Cancel
							</Button>
							<PrimaryButton
								onClick={onCreate}
								disabled={loading}
							>
								Create
							</PrimaryButton>
						</>
					}
				>
					<div className="grid gap-4">
						<Field label="Code" help="Must be unique">
							<Input
								value={createForm.code}
								onChange={(e) =>
									setCreateForm((s) => ({
										...s,
										code: e.target.value,
									}))
								}
								placeholder="e.g. compute, dedi-cpu"
							/>
						</Field>
						<Field label="Name">
							<Input
								value={createForm.name}
								onChange={(e) =>
									setCreateForm((s) => ({
										...s,
										name: e.target.value,
									}))
								}
								placeholder="Human-friendly name"
							/>
						</Field>
						<Field label="Description (optional)">
							<Textarea
								value={createForm.description}
								onChange={(e) =>
									setCreateForm((s) => ({
										...s,
										description: e.target.value,
									}))
								}
								placeholder="Short description"
							/>
						</Field>
					</div>
				</Modal>

				{/* Edit Modal */}
				<Modal
					open={!!editing}
					title={`Edit: ${editing?.name ?? editing?.code ?? "Item"}`}
					onClose={() => setEditing(null)}
					footer={
						<>
							<Button onClick={() => setEditing(null)}>
								Cancel
							</Button>
							<PrimaryButton
								onClick={onUpdate}
								disabled={loading}
							>
								Save
							</PrimaryButton>
						</>
					}
				>
					<div className="grid gap-4">
						<div className="grid gap-4 md:grid-cols-2">
							<Field label="Code">
								<Input
									value={editForm.code}
									onChange={(e) =>
										setEditForm((s) => ({
											...s,
											code: e.target.value,
										}))
									}
								/>
							</Field>
							<Field label="Name">
								<Input
									value={editForm.name}
									onChange={(e) =>
										setEditForm((s) => ({
											...s,
											name: e.target.value,
										}))
									}
								/>
							</Field>
						</div>
						<Field label="Description">
							<Textarea
								value={editForm.description}
								onChange={(e) =>
									setEditForm((s) => ({
										...s,
										description: e.target.value,
									}))
								}
							/>
						</Field>

						{editing ? (
							<Field label="Other fields (read-only)">
								<pre className="text-xs p-3 rounded-lg bg-slate-800 border border-slate-700 overflow-auto max-h-56">
									{prettyJSON(
										Object.fromEntries(
											Object.entries(
												editing || {}
											).filter(
												([k]) =>
													![
														"code",
														"name",
														"description",
													].includes(k)
											)
										)
									)}
								</pre>
							</Field>
						) : null}
					</div>
				</Modal>

				{/* Delete confirm */}
				<Modal
					open={!!confirmDel}
					title="Delete plan category"
					onClose={() => setConfirmDel(null)}
					footer={
						<>
							<Button onClick={() => setConfirmDel(null)}>
								Cancel
							</Button>
							<DangerButton onClick={onDelete} disabled={loading}>
								Delete
							</DangerButton>
						</>
					}
				>
					<p className="text-slate-300">
						Are you sure you want to delete{" "}
						<span className="font-semibold">
							{confirmDel?.name ??
								confirmDel?.code ??
								"this item"}
						</span>
						?
					</p>
				</Modal>
			</div>
		</div>
	);
};

export default PlanCategoryManager;
